import React, { forwardRef, useEffect, useRef, useState } from 'react';
import {
  Text,
  TextInput,
  StyleSheet,
  View,
  Animated,
  Easing,
  TouchableWithoutFeedback,
  Platform,
} from 'react-native';
import theme from '../../constants/theme.style.js';

const styles = StyleSheet.create({
  root: {
    position: 'relative',
    zIndex: 0,
    width: '100%',
    marginBottom: 15,
  },
  label: {
    zIndex: 1,
    position: 'absolute',
    paddingHorizontal: 5,
    borderRadius: 10,
    backgroundColor: theme.WHITE_COLOR,
    ...Platform.select({ web: { cursor: 'text' } }),
  },
  labelText: {
    fontFamily: 'Nunito_Bold',
    fontSize: 16,
    color: theme.BLACK_COLOR,
  },
  input: {
    zIndex: 0,
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 20,
    paddingRight: 20,
    borderWidth: 1,
    borderRadius: 15,
    fontFamily: 'Nunito_Regular',
    fontSize: 16,
    backgroundColor: theme.WHITE_COLOR,
    ...Platform.select({ web: { outlineStyle: 'none' } }),
  },
  error: {
    marginTop: 4,
    marginLeft: 12,
    fontSize: 12,
    color: theme.RED_ERROR_COLOR,
    fontFamily: 'Avenir-Medium',
  },
});

const TextField = forwardRef(
  (
    {
      label,
      style,
      value,
      onChangeText,
      onBlur,
      onFocus,
      error,
      keyboardType,
      textContentType,
      secureTextEntry,
      returnKeyType,
      onSubmitEditing,
      autoCapitalize = 'none',
      testID,
    },
    ref,
  ) => {
    const localRef = useRef();
    const inputRef = ref || localRef;
    const [isFocused, setIsFocused] = useState(false);

    const focusAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
      Animated.timing(focusAnim, {
        toValue: isFocused || !!value ? 1 : 0,
        duration: 150,
        easing: Easing.bezier(0.4, 0, 0.2, 1),
        useNativeDriver: true,
      }).start();
    }, [focusAnim, isFocused, value]);

    let color;
    if (error) {
      color = theme.RED_ERROR_COLOR;
    } else if (isFocused) {
      color = theme.PRIMARY_COLOR;
    } else {
      color = theme.GREY_MEDIUM_LIGHT_COLOR;
    }

    return (
      <View style={[styles.root, style]} accessibilityRole="label">
        <TouchableWithoutFeedback accessible={false} onPress={() => inputRef.current?.focus()}>
          <Animated.View
            style={[
              styles.label,
              {
                transform: [
                  {
                    scale: focusAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [1, 0.75],
                    }),
                  },
                  {
                    translateY: focusAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [16, -12],
                    }),
                  },
                  {
                    translateX: focusAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [15, 9],
                    }),
                  },
                ],
              },
            ]}
          >
            <Text
              style={[
                styles.labelText,
                {
                  color,
                },
              ]}
            >
              {label}
            </Text>
          </Animated.View>
        </TouchableWithoutFeedback>

        <TextInput
          style={[
            styles.input,
            {
              borderColor: color,
            },
          ]}
          ref={inputRef}
          value={value}
          onChangeText={onChangeText}
          onSubmitEditing={onSubmitEditing}
          onBlur={(event) => {
            setIsFocused(false);
            onBlur?.(event);
          }}
          onFocus={(event) => {
            setIsFocused(true);
            onFocus?.(event);
          }}
          keyboardType={keyboardType}
          textContentType={textContentType}
          secureTextEntry={secureTextEntry}
          returnKeyType={returnKeyType}
          autoCapitalize={autoCapitalize}
          testID={testID}
        />

        {!!error && <Text style={styles.error}>{error}</Text>}
      </View>
    );
  },
);

export default TextField;
